/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Badge, Table } from '@arcflight/tf-component-library';
import { useTable, useSortBy, usePagination } from 'react-table';
import TFTag from '../../components/TFTag/TFTag';
import defaults from '../../utils/defaults';
import AuthDropdownMenu from '../../components/AuthDropdownMenu/AuthDropdownMenu';
import { AircraftResource } from '../../models/aircraft';
import tableStyles from './WorkpacksTable.module.less';

const DocumentAssessmentTable = ({
  data,
  intl,
  toggleWorkpackDrawer,
  handleDeleteWorkpack,
  onPaginationChange,
  onSortChange,
  match,
  dateFormat,
  total,
  pageSize,
  pageIndex,
  resetToOne,
  operatorSettings,
}) => {
  const {
    userSettings: {
      details: { operators },
    },
    aircraft: { aircraftMap },
  } = useSelector((state) => state);

  const { id } = useParams();

  const defShortNameOverride = operators.find((op) => op?.id === aircraftMap.get(id)?.operator_id)?.operator_setting
    ?.defect_short_name;
  const { formatMessage } = intl;

  const columns = useMemo(
    () => [
      {
        Cell: ({ value }) => {
          let badgeValue = '';
          if (value === 'draft') badgeValue = 'grey';
          if (value === 'active') badgeValue = 'blue';
          if (value === 'pending') badgeValue = 'amber';
          return <Badge color={badgeValue} />;
        },
        Header: 'Status',
        width: 20,
        accessor: 'status',
      },
      {
        accessor: 'po_number',
        Header: operatorSettings ? `${operatorSettings.po_short_name} ${operatorSettings.po_suffix}` : 'PO Number',
        sortable: true,
        Cell: ({ value }) => {
          if (value) {
            return value;
          }
          return '-';
        },
      },
      {
        Header: 'Description',
        accessor: 'description',
        sortable: true,
        Cell: ({ value }) => {
          if (value) {
            return value;
          }
          return '-';
        },
      },
      {
        Header: 'date',
        accessor: 'date',
        sortable: true,
        Cell: ({ value }) => {
          if (value) {
            return dateFormat ? moment.utc(value).format(dateFormat) : moment(value).format(defaults.defaultDateFormat);
          }
          return '-';
        },
      },
      {
        Cell: ({ value }) => {
          if (value) {
            return dateFormat ? moment.utc(value).format(dateFormat) : moment(value).format(defaults.defaultDateFormat);
          }
          return '-';
        },
        Header: 'Release Date',
        accessor: 'release_date',
        sortable: true,
      },
      {
        Cell: ({ value }) => (
          <TFTag colour={value?.defects && value?.defects > 0 ? 'blue' : ''}>{value?.defects || '-'}</TFTag>
        ),
        Header: defShortNameOverride,
        sortable: true,
        id: 'defect',
        width: 30,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value }) => (
          <TFTag colour={value?.scheduled && value?.scheduled > 0 ? 'blue' : ''}>{value?.scheduled || '-'}</TFTag>
        ),
        width: 30,
        Header: 'SCH',
        id: 'scheduled',
        sortable: true,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value }) => <TFTag colour={value?.oop && value?.oop > 0 ? 'blue' : ''}>{value?.oop || '-'}</TFTag>,
        Header: 'OOP',
        id: 'oop',
        width: 30,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value }) => <TFTag colour={value?.llp && value?.llp > 0 ? 'blue' : ''}>{value?.llp || '-'}</TFTag>,
        Header: 'LLP',
        id: 'llp',
        width: 30,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value }) => (
          <TFTag colour={value?.extra && value?.extra > 0 ? 'blue' : ''}>{value?.extra || '-'}</TFTag>
        ),
        Header: 'EXTRA',
        id: 'extra',
        width: 30,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value }) => (
          <TFTag colour={value?.cabin_issues && value?.cabin_issues > 0 ? 'blue' : ''}>
            {value?.cabin_issues || '-'}
          </TFTag>
        ),
        Header: operatorSettings?.cabin_issue_short_name || 'Cabin',
        id: 'cabin_issues',
        width: 30,
        accessor: 'item_counts',
      },
      {
        Cell: ({ value, row }) => (
          <TFTag dataTestId={`workpack-draft-count-${row.original.id}`} colour={value?.draft_items > 0 ? 'amber' : ''}>
            {value?.draft_items || '-'}
          </TFTag>
        ),
        Header: 'Draft',
        id: 'draft',
        width: 40,
        accessor: 'item_counts',
      },
      {
        Header: '',
        id: '3dot',
        width: 20,
        Cell: ({ row }) => {
          const viewButtonChild = (
            <span className={tableStyles.workpackMenuSpan}>{formatMessage({ id: 'form.button.details' })} </span>
          );
          const complete = row?.original?.status === 'complete';
          if (complete || row.original.status === 'pending') {
            return (
              <AuthDropdownMenu
                viewText={viewButtonChild}
                handleDelete={() => handleDeleteWorkpack(row.original.id)}
                viewCallback={() => toggleWorkpackDrawer(row, 'view')}
                resource={AircraftResource.WORKPACK}
                aircraftId={match.params.id}
                options={{
                  create: false,
                  read: true,
                  update: false,
                  delete: !complete,
                }}
                forTable
              />
            );
          }

          const editButtonChild = (
            <span className={tableStyles.workpackMenuSpan}>{formatMessage({ id: 'form.button.edit' })}</span>
          );

          return (
            <>
              <AuthDropdownMenu
                viewText={viewButtonChild}
                editText={editButtonChild}
                editCallback={() => toggleWorkpackDrawer(row, 'edit')}
                viewCallback={() => toggleWorkpackDrawer(row, 'view')}
                handleDelete={() => handleDeleteWorkpack(row.original.id)}
                resource={AircraftResource.WORKPACK}
                aircraftId={match.params.id}
                options={{ create: false, read: true, update: true, delete: true }}
                forTable
              />
            </>
          );
        },
      },
    ],
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      manualSortBy: true,
      initialState: { pageIndex, pageSize },
      pageCount: Math.ceil(total / (pageSize || 10)),
    },
    useSortBy,
    usePagination,
  );
  return (
    <div>
      <div className={tableStyles.tableContainer}>
        <Table
          tableInstance={tableInstance}
          handleRowClick={(row) => toggleWorkpackDrawer(row, 'view')}
          total={total}
          handlePaginationChange={(currentPage, numberOfItems) => onPaginationChange(currentPage, numberOfItems)}
          passedPageSize={pageSize}
          handleSortChange={(test) => onSortChange(test)}
          resetToOne={resetToOne}
        />
      </div>
    </div>
  );
};

DocumentAssessmentTable.propTypes = {
  intl: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  toggleWorkpackDrawer: PropTypes.func.isRequired,
  handleDeleteWorkpack: PropTypes.func.isRequired,
};

export default injectIntl(DocumentAssessmentTable);
